import { CheckCircleIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { RegionId } from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { useActivityTypeOptions } from "components/tasks/activity-type-options";
import { ArrivingListItem, useArrivals } from "components/ui/list-motion";
import { AnimatePresence } from "framer-motion";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNow, useRegionId } from "store/selectors/hooks";
import { agendaSlice } from "store/slices/agenda";
import { ApplicationState } from "store/state";
import { MyActivityItem, todayRange } from "./my-activity";
import { MyActivityEditingPanels, useMyActivityEditing } from "./my-activity-editing";
import { MyActivityItemRow, toMyActivityItem } from "./my-activity-item-row";
import { useAgendaRefresh } from "./use-agenda-refresh";

const cardClasses = "overflow-hidden rounded-lg bg-white ring-1 ring-gray-300";

export const AgendaCompletedToday: React.FC<unknown> = () => {
    const now = useNow();
    const regionId = useRegionId();
    const trpcClient = trpc.useTRPC();
    const dispatch = useDispatch();
    const open = useSelector(
        (state: ApplicationState) => state.agenda.sectionVisibility.completedToday,
    );
    const input = { ...todayRange(now), regionId: regionId ?? ("" as RegionId) };
    const activityTypes = useActivityTypeOptions();
    const refreshAgenda = useAgendaRefresh();
    const editing = useMyActivityEditing();

    const activityQuery = useQuery({
        ...trpcClient.touchpoint.myActivity.queryOptions(input),
        enabled: !!regionId,
    });

    const items: MyActivityItem[] = (activityQuery.data ?? []).map(toMyActivityItem);
    const itemIds = React.useMemo(
        () => (activityQuery.data ? activityQuery.data.map(item => item.id) : null),
        [activityQuery.data],
    );
    const arrivals = useArrivals(itemIds);

    if (!activityQuery.isSuccess) {
        return null;
    }

    return (
        <div className={items.length === 0 ? "hidden" : cardClasses}>
            <button
                type="button"
                onClick={() =>
                    dispatch(agendaSlice.actions.toggleAgendaSection("completedToday"))
                }
                className={`flex w-full items-center gap-2 px-4 py-3 hover:bg-gray-50 ${
                    open ? "border-b border-gray-200" : ""
                }`}>
                <ChevronDownIcon
                    className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${
                        open ? "rotate-0" : "-rotate-90"
                    }`}
                    aria-hidden="true"
                />
                <span className="h-5 w-1 flex-shrink-0 rounded-full bg-green-500" />
                <CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-green-600" />
                <span className="text-sm font-semibold text-gray-800">
                    Completed today
                </span>
                <span className="text-xs text-gray-500">{items.length}</span>
            </button>
            {open ? (
                <ul className="divide-y divide-gray-100">
                    <AnimatePresence initial={false}>
                        {items.map(item => (
                            <ArrivingListItem
                                key={item.id}
                                isNew={arrivals.has(item.id)}
                                className="px-4">
                                <MyActivityItemRow
                                    item={item}
                                    activityTypes={activityTypes}
                                    onSaved={refreshAgenda}
                                    onEdit={editing.onEditFor(item)}
                                />
                            </ArrivingListItem>
                        ))}
                    </AnimatePresence>
                </ul>
            ) : null}
            <MyActivityEditingPanels editing={editing} />
        </div>
    );
};
